import { useEffect, useState, useSyncExternalStore } from "react";
import {
  bindToolCloseTracking,
  initAnalytics,
  trackToolOpen,
} from "../analytics";
import { useToolUsage } from "../usage/useToolUsage";
import {
  getPortalAuthErrorCode,
  messageForPortalAuthError,
  type PortalAuthErrorCode,
} from "../lib/portal-auth-errors";
import {
  getDevAccessOverrideSnapshot,
  isDevAccessEnabled,
  subscribeDevAccessOverride,
  type DevAccessOverride,
} from "../lib/dev-access-override";
import type { AccessLevel } from "../portal-auth";

export interface UseAccessState {
  access: AccessLevel | null;
  name: string | null;
  loading: boolean;
  error: string | null;
  errorCode: PortalAuthErrorCode | null;
  isPaid: boolean;
  isFree: boolean;
  isGuest: boolean;
  toolId: string | null;
  toolPriceAmount: string | null;
  devOverride: DevAccessOverride | null;
}

function applyDevOverride(
  portalAccess: AccessLevel | null,
  portalError: string | null,
  isGuest: boolean,
  override: DevAccessOverride | null,
): { access: AccessLevel | null; error: string | null; isGuest: boolean } {
  if (!isDevAccessEnabled() || override === null) {
    return { access: portalAccess, error: portalError, isGuest };
  }

  if (override === "paid") {
    return { access: "PAID", error: null, isGuest: false };
  }
  if (override === "free") {
    return { access: "FREE", error: null, isGuest: false };
  }
  if (override === "guest") {
    return { access: "FREE", error: null, isGuest: true };
  }
  return { access: portalAccess, error: portalError, isGuest };
}

export function useAccess(): UseAccessState {
  const [portalAccess, setPortalAccess] = useState<AccessLevel | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [portalIsGuest, setPortalIsGuest] = useState(false);
  const [toolId, setToolId] = useState<string | null>(null);
  const [toolPriceAmount, setToolPriceAmount] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [portalErrorCode, setPortalErrorCode] = useState<PortalAuthErrorCode | null>(null);

  const devOverride = useSyncExternalStore(
    subscribeDevAccessOverride,
    getDevAccessOverrideSnapshot,
  );

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!window.PortalAuth) {
        if (!cancelled) {
          setPortalError("Portal Auth SDK niet geladen.");
          setPortalErrorCode(null);
          setLoading(false);
        }
        return;
      }

      try {
        await initAnalytics();
        const result = await window.PortalAuth.init();
        if (cancelled) return;
        setPortalAccess(result.access);
        setName(result.name);
        setPortalIsGuest(Boolean(result.guest));
        setToolId(result.toolId ?? null);
        setToolPriceAmount(result.priceAmount ?? null);
        setPortalError(null);
        setPortalErrorCode(null);

        const slug = import.meta.env.VITE_TOOL_SLUG as string | undefined;
        if (slug) {
          trackToolOpen(slug, result.access, { guest: Boolean(result.guest) });
        }
      } catch (err) {
        if (cancelled) return;
        setPortalAccess("NONE");
        setName(null);
        setPortalIsGuest(false);
        setToolId(null);
        setToolPriceAmount(null);
        const code = getPortalAuthErrorCode(err);
        setPortalErrorCode(code);
        setPortalError(messageForPortalAuthError(code));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const slug = import.meta.env.VITE_TOOL_SLUG as string | undefined;
  const ready = !loading && portalAccess !== null;
  useToolUsage({ slug: slug ?? "", ready });

  useEffect(() => {
    if (!slug || portalAccess === null) return;
    return bindToolCloseTracking(slug);
  }, [portalAccess, slug]);

  const { access, error, isGuest } = applyDevOverride(
    portalAccess,
    portalError,
    portalIsGuest,
    devOverride,
  );

  return {
    access,
    name,
    loading,
    error,
    errorCode: portalErrorCode,
    isPaid: access === "PAID",
    isFree: access === "FREE",
    isGuest,
    toolId,
    toolPriceAmount,
    devOverride,
  };
}
